(ns kyber.components.composite.tab-bar-container
  (:require [kyber.styles.core :refer [gs styles->classes]]))

(def tab-styles
  {:font-weight (gs [:font-weight :caption-30])
   :font-size (gs [:font-size :caption-30])
   :text-decoration :none
   :padding [(gs [:spacing :p12])
             (gs [:spacing :p20])]
   :border-radius [[(gs [:radius :r3])
                    (gs [:radius :r3])
                    (gs [:radius :r0])
                    (gs [:radius :r0])]]
   :transition "background-color 0.2s ease-in-out"
   :border (str "1px solid " (gs [:colors :gray-scale-100]))
   :border-bottom :none
   :border-right :none

   "&:last-child"
   {:border-right (str "1px solid " (gs [:colors :gray-scale-100]))}})

(def link-styles
  {:font-weight (gs [:font-weight :caption-30])
   :font-size (gs [:font-size :caption-30])
   :text-decoration :none
   :text-transform :uppercase
   :padding [(gs [:spacing :p12])
             (gs [:spacing :p20])]
   :transition "color 0.2s ease-in-out"})

(def breadcrumb-styles
  {:font-weight (gs [:font-weight :caption-30])
   :font-size (gs [:font-size :title-20])
   :text-decoration :underline
   :text-transform :capitalize
   :padding [(gs [:spacing :p12])
             (gs [:spacing :p20])]
   :transition "color 0.2s ease-in-out"

   "&:after"
   {:content "\">>\""
    :margin-left (gs [:spacing :p20])}

   "&:last-child"
   {"&:after"
    {:display :none}}})

(def classes
  (styles->classes
   {:tab-container
    {:display :flex
     :background-color (gs [:colors :true-white])
     :position :sticky
     :top (gs [:spacing :p48])
     :z-index 2}
    
    :indented-tab-container
    {:margin-left (gs [:spacing :p20])}

    :body-container
    {:padding-top (gs [:spacing :p20])}

    :button-tab-container
    {:box-shadow (gs [:shadows :shadow-6])
     :background-color (gs [:colors :true-white])
     :padding (gs [:spacing :p20])}

    :remove-body-padding
    {:padding-left (gs [:spacing :p0])
     :padding-right (gs [:spacing :p0])
     :padding-bottom (gs [:spacing :p0])}

    :active-tab
    (merge
     tab-styles
     {:background-color (gs [:colors :true-white])
      :color (gs [:colors :primary-lite-600])
      :cursor :default})

    :inactive-tab
    (merge
     tab-styles
     {:background-color (gs [:colors :primary-lite-500])
      :color (gs [:colors :true-white])

      "&:hover"
      {:background-color (gs [:colors :primary-lite-600])}})

    :active-link
    (merge
     link-styles
     {:cursor :default
      :color (gs [:colors :gray-scale-400])
      :border-bottom (str "8px solid " (gs [:colors :gray-scale-200]))})

    :inactive-link
    (merge
     link-styles
     {:color (gs [:colors :primary-lite-500])

      "&:hover"
      {:color (gs [:colors :primary-lite-600])}})

    :active-bradcrumb
    (merge
     breadcrumb-styles
     {:cursor :default
      :color (gs [:colors :gray-scale-400])
      :border-bottom (str "8px solid " (gs [:colors :gray-scale-200]))})

    :inactive-bradcrumb
    (merge
     breadcrumb-styles
     {:color (gs [:colors :primary-lite-500])

      "&:hover"
      {:color (gs [:colors :primary-lite-600])}})

    :disabled-button
    {:color (str (gs [:colors :gray-scale-300]) " !important")
     :background (str (gs [:colors :gray-scale-50]) " !important")
     :cursor :default}

    :disabled-link
    {:color (str (gs [:colors :gray-scale-300]) " !important")
     :cursor :default}}))

(defn- is-active?
  [key active-key]
  (if (set? key)
    (contains? key active-key)
    (= key active-key)))

(defn- render-tab
  [{:keys [tab is-active tab-style]}]
  (let [{:keys [display route disabled]} tab]
    [:a (cond-> {:href route
                 :role "tab"
                 :aria-selected (str is-active)
                 :class (cond-> []
                          (and (true? is-active)
                               (= tab-style :button))
                          (conj (:active-tab classes))

                          (and (false? is-active)
                               (= tab-style :button))
                          (conj (:inactive-tab classes))

                          (and (true? is-active)
                               (= tab-style :breadcrumbs))
                          (conj (:active-bradcrumb classes))

                          (and (false? is-active)
                               (= tab-style :breadcrumbs))
                          (conj (:inactive-bradcrumb classes))

                          (and (true? is-active)
                               (= tab-style :link))
                          (conj (:active-link classes))

                          (and (false? is-active)
                               (= tab-style :link))
                          (conj (:inactive-link classes))

                          (and (true? disabled)
                               (= tab-style :link))
                          (conj (:disabled-link classes))

                          (and (true? disabled)
                               (= tab-style :button))
                          (conj (:disabled-button classes)))}
          (true? disabled)
          (assoc :href "javascript:;"))
     display]))

(defn render
  [{:keys [tabs active-tab tab-style no-body-padding indent-tabs data-test]
    :or {tab-style :button
         no-body-padding false
         indent-tabs false
         data-test "tab-bar-container"}}]
  (let [{active-tab-view :view
         active-tab-arguments :arguments}
        (->> tabs
             (filter #(is-active? (:key %) active-tab))
             (first))

        arguments
        (cond
          (fn? active-tab-arguments) (active-tab-arguments)
          (vector? active-tab-arguments) active-tab-arguments
          :else [])]

    (when (some? active-tab-view)
      [:div
       [:ul {:class (cond-> [(:tab-container classes)]
                      (true? indent-tabs)
                      (conj (:indented-tab-container classes)))
              :role "tablist"}
        (for [{:keys [key] :as tab} (remove #(true? (:hide %)) tabs)]
          ^{:key key :data-test data-test}
          [render-tab {:tab tab
                       :tab-style tab-style
                       :is-active (is-active? key active-tab)}])]
       [:div
        {:class (cond-> [(:body-container classes)]
                  (true? (= tab-style :button))
                  (conj (:button-tab-container classes))

                  (true? no-body-padding)
                  (conj (:remove-body-padding classes)))}
        [into [active-tab-view] arguments]]])))
